import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { ContentBlock } from '@/mocks/articles';
import { articles } from '@/mocks/articles';
import SiteLayout from '@/components/feature/SiteLayout';
import Seo from '@/components/feature/Seo';
import { getCategory, formatDate, formatViews } from '@/utils/content';

function Block({ block }: { block: ContentBlock }) {
  if (block.type === 'h2') {
    return (
      <h2 className="mt-9 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
        {block.text}
      </h2>
    );
  }
  if (block.type === 'ul') {
    return (
      <ul className="mt-4 space-y-3">
        {block.items.map((text) => (
          <li key={text} className="flex gap-3 text-[15px] leading-relaxed text-foreground-700">
            <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"></span>
            <span>{text}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === 'quote') {
    return (
      <blockquote className="mt-6 rounded-r-lg border-l-4 border-accent-500 bg-accent-50/70 px-5 py-4 text-[15px] italic leading-relaxed text-accent-900">
        {block.text}
      </blockquote>
    );
  }
  return <p className="mt-4 text-[15px] leading-[1.9] text-foreground-700">{block.text}</p>;
}

export default function ArticleDetail() {
  const { id } = useParams();
  const article = articles.find((item) => item.id === id);
  const [copied, setCopied] = useState(false);

  const headingBlocks = useMemo(() => {
    if (!article) return [] as { index: number; text: string }[];
    return article.content
      .map((block, index) => ({ block, index }))
      .filter((entry) => entry.block.type === 'h2')
      .map((entry) => ({ index: entry.index, text: entry.block.type === 'h2' ? entry.block.text : '' }));
  }, [article]);

  const related = useMemo(() => {
    if (!article) return [];
    const sameCategory = articles.filter((item) => item.id !== article.id && item.categoryId === article.categoryId);
    const others = articles.filter((item) => item.id !== article.id && item.categoryId !== article.categoryId);
    return [...sameCategory, ...others].slice(0, 3);
  }, [article]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!article) {
    return (
      <SiteLayout>
      <Seo
        title="专题未找到 | 瓜田黑料"
        description="该专题链接可能已被更新或替换，请前往瓜田黑料专题列表浏览最新的时间线复盘内容。"
        keywords="瓜田黑料,专题未找到,时间线复盘"
        path="/article"
      />
      <div className="flex min-h-[70vh] flex-col items-center justify-center bg-background-50 px-4 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-600">
          <i className="ri-file-unknow-line text-3xl"></i>
        </span>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">这篇复盘暂时找不到了</h1>
        <p className="mt-3 max-w-md text-sm text-foreground-600">
          链接可能已经被更新或替换。你可以回到专题列表，从最新整理的内容开始看起。
        </p>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <Link to="/topics" className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">
            浏览全部专题
          </Link>
          <Link to="/" className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 px-6 py-2.5 text-sm text-foreground-800 hover:bg-background-100">
            返回首页
          </Link>
        </div>
      </div>
      </SiteLayout>
    );
  }

  const category = getCategory(article.categoryId);
  const articleJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: article.title,
      description: article.excerpt,
      datePublished: article.date,
      dateModified: article.date,
      inLanguage: 'zh-CN',
      author: { '@type': 'Person', name: article.author },
      publisher: { '@type': 'Organization', name: '瓜田黑料' },
      articleSection: category?.name,
      keywords: article.tags.join(','),
      mainEntityOfPage: `https://changshazy.cn/article/${article.id}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://changshazy.cn/' },
        { '@type': 'ListItem', position: 2, name: '全部专题', item: 'https://changshazy.cn/topics' },
        { '@type': 'ListItem', position: 3, name: article.title, item: `https://changshazy.cn/article/${article.id}` },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title={`${article.title} | 瓜田黑料`}
        description={article.excerpt}
        keywords={[article.tags.join(','), category?.name ?? '', '瓜田黑料'].filter(Boolean).join(',')}
        path={`/article/${article.id}`}
        type="article"
        jsonLd={articleJsonLd}
      />
      <section className="border-b border-background-200 bg-background-100/60">
        <div className="mx-auto w-full max-w-5xl px-4 py-8 md:px-6 md:py-12">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="cursor-pointer hover:text-primary-700">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/topics" className="cursor-pointer hover:text-primary-700">全部专题</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/topics?category=${article.categoryId}`} className="cursor-pointer hover:text-primary-700">
              {category?.name}
            </Link>
          </nav>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <Link
              to={`/topics?category=${article.categoryId}`}
              className="cursor-pointer whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900"
            >
              {category?.name}
            </Link>
            {article.tags.map((tag) => (
              <span key={tag} className="whitespace-nowrap rounded-full border border-background-300 px-3 py-1 text-xs text-foreground-600">
                {tag}
              </span>
            ))}
          </div>

          <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
            {article.title}
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {article.subtitle}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500 md:text-sm">
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
            <span className="whitespace-nowrap">{formatDate(article.date)}</span>
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-time-line"></i>
              {article.readTime} 分钟阅读
            </span>
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-eye-line"></i>
              {formatViews(article.views)} 次浏览
            </span>
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
          <div className="relative mb-8 h-[220px] w-full overflow-hidden rounded-lg md:h-[380px]">
            <img src={article.cover} alt={article.coverAlt} className="h-full w-full object-cover object-top" />
          </div>

          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <article className="lg:col-span-8">
              {article.excerpt ? (
                <p className="rounded-lg bg-background-100/70 p-5 text-[15px] leading-relaxed text-foreground-700">
                  {article.excerpt}
                </p>
              ) : null}

              <div className="mt-6">
                {article.content.map((block, index) => (
                  <div key={index} id={`section-${index}`}>
                    <Block block={block} />
                  </div>
                ))}
              </div>

              <div className="mt-10 rounded-lg border border-background-200 bg-background-100/50 p-5 md:p-6">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                    <i className="ri-timeline-view text-sm"></i>
                  </span>
                  事件时间线速览
                </h2>
                <ol className="mt-5 space-y-5">
                  {article.timeline.map((node) => (
                    <li key={`${node.time}-${node.text}`} className="relative flex gap-4 pl-1">
                      <span className="relative flex flex-col items-center">
                        <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-accent-500"></span>
                        <span className="mt-1 w-px flex-1 bg-background-300"></span>
                      </span>
                      <span className="pb-1">
                        <span className="block text-xs font-medium tracking-wide text-primary-700">{node.time}</span>
                        <span className="mt-1 block text-sm leading-relaxed text-foreground-700">{node.text}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-background-200 pt-6">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-foreground-500">标签</span>
                  {article.tags.map((tag) => (
                    <span key={tag} className="whitespace-nowrap rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-700">
                      {tag}
                    </span>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border border-background-300 px-4 py-2 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:bg-primary-50"
                >
                  <i className={copied ? 'ri-check-line' : 'ri-link'}></i>
                  {copied ? '链接已复制' : '复制本文链接'}
                </button>
              </div>
            </article>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24">
                <div className="rounded-lg border border-background-200 bg-background-100/50 p-5">
                  <h2 className="font-heading text-sm font-bold tracking-wide text-foreground-950">本文目录</h2>
                  <ol className="mt-4 space-y-2.5 text-sm">
                    {headingBlocks.map((item) => (
                      <li key={item.index}>
                        <a
                          href={`#section-${item.index}`}
                          className="flex cursor-pointer gap-2 text-foreground-600 transition-colors hover:text-primary-700"
                        >
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-background-200 text-[11px] text-foreground-700">
                            {headingBlocks.indexOf(item) + 1}
                          </span>
                          <span>{item.text}</span>
                        </a>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-sm font-bold tracking-wide text-foreground-950">相关阅读</h2>
                  <ul className="mt-4 space-y-4">
                    {related.map((item) => (
                      <li key={item.id}>
                        <Link to={`/article/${item.id}`} className="group block cursor-pointer">
                          <span className="block font-heading text-sm font-semibold leading-snug text-foreground-900 transition-colors group-hover:text-primary-700">
                            {item.title}
                          </span>
                          <span className="mt-1 block text-xs text-foreground-500">
                            {getCategory(item.categoryId)?.name} · {formatDate(item.date)}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-5 rounded-lg bg-secondary-900 p-5">
                  <h2 className="font-heading text-sm font-bold text-secondary-50">有更完整的材料？</h2>
                  <p className="mt-2 text-xs leading-relaxed text-secondary-200">
                    带上原始链接和时间点投稿，我们会重新核对这条时间线。
                  </p>
                  <Link
                    to="/contact"
                    className="mt-4 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-send-plane-line"></i>
                    我要投稿
                  </Link>
                </div>
              </div>
            </aside>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-background-200 pt-6">
            <Link
              to="/topics"
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border border-background-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:bg-background-100"
            >
              <i className="ri-arrow-left-line"></i>
              返回专题列表
            </Link>
            <Link
              to={`/topics?category=${article.categoryId}`}
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border border-background-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:bg-background-100"
            >
              更多「{category?.name}」
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}